import { type GridColDef, type GridRenderCellParams } from "@mui/x-data-grid-pro";

import { customVersionComparator } from "../../../../helpers/coappDataGridUtils";
import CoAppDataGridDateTimeCell from "../../../global/components/datagrid/CoAppDataGridDateTimeCell";
import CoAppDataGridNameCell from "../../../global/components/datagrid/CoAppDataGridNameCell";
import { WorkstationSwitch } from "../styled/workstation.styled";

export const workstationColumns = ["serialNumber", "killSwitchActive", "hostName", "ip", "macAddress", "lastLogUpload", "lastPhoneHome"];

export interface WorkstationRow {
	id: string | number;
	uuid?: string;
	serialNumber?: string | null;
	killSwitchActive?: boolean;
	hostName?: string | null;
	ip?: string | null;
	macAddress?: string | null;
	extensionVersion?: string | null;
	lastLogUpload?: string | null;
	lastPhoneHome?: string | null;
	companionVersion?: string | null;
	[key: string]: unknown;
}

export const getWorkstationManagementColumns = (
	handleKillSwitchModalToggle: (row: WorkstationRow, isActive: boolean) => void,
): GridColDef<WorkstationRow>[] => [
	{
		field: "serialNumber",
		headerName: "Serial Number",
		sortable: true,
		flex: 1,
		editable: false,
		valueGetter: (value, row) => {
			const v = value ?? row?.serialNumber;
			return String(v)?.trim() ?? "";
		},
		renderCell: (params: GridRenderCellParams<WorkstationRow, string>) => (
			<CoAppDataGridNameCell item={{ name: params.value }} />
		)
	},
	{
		field: "killSwitchActive",
		headerName: "Kill Switch Status",
		sortable: true,
		flex: 1,
		editable: true,
		type: "boolean",
		renderCell: (params: GridRenderCellParams<WorkstationRow, boolean | undefined>) => (
			<WorkstationSwitch
						checked={Boolean(params.row?.killSwitchActive)}
						disabled={false}
						onChange={() => handleKillSwitchModalToggle(params.row, Boolean(params.row.killSwitchActive))}
					/>
		)
	},
	{
		field: "hostName",
		sortable: true,
		headerName: "Computer Name",
		flex: 1,
		editable: false,
		valueGetter: (value, row) => {
			const v = value ?? row?.hostName;
			return String(v)?.trim() ?? "";
		},
		renderCell: (params: GridRenderCellParams<WorkstationRow, string>) => (
			<CoAppDataGridNameCell item={{ name: params.value }} />
		)
	},
	{
		field: "ip",
		sortable: true,
		headerName: "Internal IP",
		flex: 1,
		editable: false,
		valueGetter: (value, row) => {
			const v = value ?? row?.ip;
			return String(v)?.trim() ?? "";
		},
		renderCell: (params: GridRenderCellParams<WorkstationRow, string>) => (
			<CoAppDataGridNameCell item={{ name: params.value }} />
		)
	},
	{
		field: "comment",
		sortable: true,
		headerName: "Comment",
		flex: 1,
		editable: true,
		valueGetter: (value, row) => {
			const v = value ?? row?.comment;
			return String(v)?.trim() ?? "";
		},
		renderCell: (params: GridRenderCellParams<WorkstationRow, string>) => (
			<CoAppDataGridNameCell item={{ name: params.value }} />
		)
	},
	{
		field: "macAddress",
		sortable: true,
		headerName: "Mac Address",
		flex: 1,
		editable: false,
		valueGetter: (value, row) => {
			const v = value ?? row?.macAddress;
			return String(v)?.trim() ?? "";
		},
		renderCell: (params: GridRenderCellParams<WorkstationRow, string>) => (
			<CoAppDataGridNameCell item={{ name: params.value }} />
		)
	},
	{
		field: "extensionVersion",
		headerName: "Extension Version",
		flex: 1,
		editable: false,
		sortComparator: customVersionComparator,
		valueGetter: (value, row) => {
			const v = value ?? row?.extensionVersion;
			return String(v)?.trim() ?? "";
		},
		renderCell: (params: GridRenderCellParams<WorkstationRow, string>) => (
			<CoAppDataGridNameCell item={{ name: params.value }} />
		)
	},
	{
		field: "lastLogUpload",
		headerName: "Last Log Upload",
		sortable: true,
		flex: 1,
		editable: false,
		valueGetter: (value, row) => {
			const v = value ?? row?.lastLogUpload;
			if (!v) {
				return null;
			}
			return new Date(v as string);
		},
		renderCell: (params: GridRenderCellParams<WorkstationRow, Date | null>) => (
			<CoAppDataGridDateTimeCell value={params.value} />
		)
	},
	{
		field: "lastPhoneHome",
		headerName: "Last extension heartbeat",
		sortable: true,
		flex: 1,
		editable: false,
		valueGetter: (value, row) => {
			const v = value ?? row?.lastPhoneHome;
			if (!v) {
				return null;
			}
			return new Date(v as string);
		},
		renderCell: (params: GridRenderCellParams<WorkstationRow, Date | null>) => (
			<CoAppDataGridDateTimeCell value={params.value} />
		)
	},
	{
		field: "companionVersion",
		headerName: "Companion Version",
		flex: 1,
		editable: false,
		sortComparator: customVersionComparator,
		valueGetter: (value, row) => {
			const v = value ?? row?.companionVersion;
			return String(v)?.trim() ?? "";
		},
		renderCell: (params: GridRenderCellParams<WorkstationRow, string>) => (
			<CoAppDataGridNameCell item={{ name: params.value }} />
		)
	}
];
